"use client";

import {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";

type ThemeMode = "light" | "dark" | "system";

interface ThemeProviderProps {
	children: ReactNode;
	defaultTheme?: ThemeMode;
	enableSystem?: boolean;
	attribute?: "class" | "data-theme";
}

interface ThemeContextValue {
	theme: ThemeMode;
	resolvedTheme: "light" | "dark";
	setTheme: (theme: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = "theme";

function resolveSystemTheme() {
	if (typeof window === "undefined") return "light" as const;
	return window.matchMedia("(prefers-color-scheme: dark)").matches
		? "dark"
		: "light";
}

function applyThemeToDocument(
	attribute: "class" | "data-theme",
	resolvedTheme: "light" | "dark",
) {
	const root = document.documentElement;

	if (attribute === "class") {
		root.classList.remove("light", "dark");
		root.classList.add(resolvedTheme);
		return;
	}

	root.setAttribute("data-theme", resolvedTheme);
}

export function ThemeProvider({
	children,
	defaultTheme = "system",
	enableSystem = true,
	attribute = "class",
}: ThemeProviderProps) {
	const [theme, setTheme] = useState<ThemeMode>(defaultTheme);
	const [resolvedTheme, setResolvedTheme] = useState<"light" | "dark">(() =>
		defaultTheme === "dark"
			? "dark"
			: defaultTheme === "light"
				? "light"
				: resolveSystemTheme(),
	);

	useEffect(() => {
		const stored = localStorage.getItem(STORAGE_KEY) as ThemeMode | null;
		const nextTheme = stored ?? defaultTheme;
		setTheme(nextTheme);
	}, [defaultTheme]);

	useEffect(() => {
		const finalTheme =
			theme === "system" && enableSystem ? resolveSystemTheme() : theme;
		const normalized = (finalTheme === "dark" ? "dark" : "light") as
			| "light"
			| "dark";

		setResolvedTheme(normalized);
		applyThemeToDocument(attribute, normalized);
		document.documentElement.style.colorScheme = normalized;

		if (theme === "system") {
			localStorage.removeItem(STORAGE_KEY);
		} else {
			localStorage.setItem(STORAGE_KEY, theme);
		}
	}, [theme, enableSystem, attribute]);

	useEffect(() => {
		if (!enableSystem || theme !== "system") return;

		const media = window.matchMedia("(prefers-color-scheme: dark)");
		const onChange = () => {
			const nextResolved = media.matches ? "dark" : "light";
			setResolvedTheme(nextResolved);
			applyThemeToDocument(attribute, nextResolved);
			document.documentElement.style.colorScheme = nextResolved;
		};

		media.addEventListener("change", onChange);
		return () => media.removeEventListener("change", onChange);
	}, [theme, enableSystem, attribute]);

	const value = useMemo(
		() => ({ theme, resolvedTheme, setTheme }),
		[theme, resolvedTheme],
	);

	return (
		<ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
	);
}

export function useTheme() {
	const context = useContext(ThemeContext);
	if (!context) {
		throw new Error("useTheme must be used within ThemeProvider");
	}
	return context;
}
